import axios from "axios";

import type {
	DocumentTemplate,
	DocumentTemplatePartScope,
	DocumentTemplateScope,
	DocumentTemplateUploadResponse,
	GenerateDeckFromTemplateRequest,
	GenerateDocumentDeckSource,
	GenerateDocumentFromTemplatePartResponse,
	GenerateDocumentRequest,
	GeneratedDocument,
	Paginated,
	ReadyDocumentTemplatePart,
	UpdateDocumentTemplateBody,
	UpdateDocumentTemplatePartsBody,
} from "@virtual-brain-lib/types";

import env from "../environments";
import { infiniteQueryOptions, queryOptions } from "../query-client";
import { QUERY_KEYS } from "../query-client/queryKeys";

export async function uploadDocumentTemplate({
	file,
	fileSize,
	scope,
}: {
	file: File;
	fileSize: number;
	scope: DocumentTemplateScope;
}): Promise<DocumentTemplateUploadResponse> {
	const formData = new FormData();

	formData.append("file", file);
	formData.append("fileSize", fileSize.toString());
	formData.append("scope", scope);

	const response = await axios.post<DocumentTemplateUploadResponse>(
		`${env.apiUrl}/document-templates`,
		formData,
		{
			headers: {
				// eslint-disable-next-line @typescript-eslint/naming-convention
				"Content-Type": "multipart/form-data",
			},
		},
	);

	return response.data;
}

export const getCompanyDocumentTemplates = queryOptions({
	queryKey: "companyDocumentTemplates",
	async queryFn() {
		const response = await axios.get<DocumentTemplate[]>(`${env.apiUrl}/document-templates`, {
			params: { scope: "company" satisfies DocumentTemplateScope },
		});

		return response.data;
	},
});

export const getPersonalDocumentTemplates = queryOptions({
	queryKey: "personalDocumentTemplates",
	async queryFn() {
		const response = await axios.get<DocumentTemplate[]>(`${env.apiUrl}/document-templates`, {
			params: { scope: "personal" satisfies DocumentTemplateScope },
		});

		return response.data;
	},
});

export async function fetchDocumentTemplateParts(params: {
	search?: string;
	limit?: number;
	offset?: number;
	templateId?: string;
	scope?: DocumentTemplatePartScope;
}): Promise<Paginated<ReadyDocumentTemplatePart>> {
	const response = await axios.get<Paginated<ReadyDocumentTemplatePart>>(
		`${env.apiUrl}/document-template-parts`,
		{ params },
	);

	return response.data;
}

export const getDocumentTemplatePartsInfinite = infiniteQueryOptions({
	queryKey: "documentTemplateParts",
	async queryFn({ queryKey, pageParam }) {
		const [, params] = queryKey;

		return fetchDocumentTemplateParts({ ...params, offset: pageParam });
	},
	initialPageParam: 0,
	getNextPageParam(lastPage, allPages) {
		const loadedCount = allPages.flatMap((page) => page.items).length;

		return loadedCount < lastPage.countTotal ? loadedCount : undefined;
	},
});

export const getRecommendedDocumentTemplateParts = queryOptions({
	queryKey: "recommendedDocumentTemplateParts",
	async queryFn({ queryKey }) {
		const [, params] = queryKey;
		const response = await axios.get<ReadyDocumentTemplatePart[]>(
			`${env.apiUrl}/document-template-parts/recommendations`,
			{ params },
		);

		return response.data;
	},
});

export async function generateDeckFromTemplate(body: GenerateDeckFromTemplateRequest) {
	const response = await axios.post<GenerateDocumentFromTemplatePartResponse>(
		`${env.apiUrl}/document-template-parts/generate-deck`,
		body,
	);

	return response.data;
}

export const getDocumentTemplateDecks = queryOptions({
	queryKey: "documentTemplateDecks",
	async queryFn() {
		const response = await axios.get<DocumentTemplate[]>(
			`${env.apiUrl}/document-template-parts/decks`,
		);

		return response.data;
	},
});

export async function getAdminDocumentTemplateParts(params: {
	readonly templateId: string;
	readonly limit?: number;
	readonly offset?: number;
}): Promise<Paginated<ReadyDocumentTemplatePart>> {
	const response = await axios.get<Paginated<ReadyDocumentTemplatePart>>(
		`${env.apiUrl}/document-template-parts`,
		{
			params: {
				...params,
				includeUnpublishedParts: true,
				scope: "company" satisfies DocumentTemplatePartScope,
			},
		},
	);

	return response.data;
}

export async function getPersonalDocumentTemplateParts(params: {
	readonly templateId: string;
	readonly limit?: number;
	readonly offset?: number;
}): Promise<Paginated<ReadyDocumentTemplatePart>> {
	const response = await axios.get<Paginated<ReadyDocumentTemplatePart>>(
		`${env.apiUrl}/document-template-parts`,
		{
			params: {
				...params,
				includeUnpublishedParts: true,
				scope: "personal" satisfies DocumentTemplatePartScope,
			},
		},
	);

	return response.data;
}

export function getAdminDocumentTemplatePartsInfinite(params: {
	readonly templateId: string | undefined;
	readonly limit: number;
}) {
	const { templateId, limit } = params;

	return {
		queryKey: QUERY_KEYS.adminDocumentTemplateParts({ templateId: templateId ?? "" }),
		async queryFn({ pageParam }: { pageParam: number }) {
			return getAdminDocumentTemplateParts({
				templateId: templateId ?? "",
				limit,
				offset: pageParam,
			});
		},
		getNextPageParam(
			lastPage: Paginated<ReadyDocumentTemplatePart>,
			allPages: Array<Paginated<ReadyDocumentTemplatePart>>,
		) {
			if (lastPage.items.length < limit) {
				return undefined;
			}

			return allPages.flatMap((page) => page.items).length;
		},
		initialPageParam: 0,
		enabled: templateId !== undefined,
		// Default staleTime is 0: reopening the modal re-enables the query and TanStack
		// refetches every page already in cache in parallel. Keep data fresh until invalidated.
		staleTime: Number.POSITIVE_INFINITY,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
	};
}

export function getPersonalDocumentTemplatePartsInfinite(params: {
	readonly templateId: string | undefined;
	readonly limit: number;
}) {
	const { templateId, limit } = params;

	return {
		queryKey: QUERY_KEYS.personalDocumentTemplateParts({ templateId: templateId ?? "" }),
		async queryFn({ pageParam }: { pageParam: number }) {
			return getPersonalDocumentTemplateParts({
				templateId: templateId ?? "",
				limit,
				offset: pageParam,
			});
		},
		getNextPageParam(
			lastPage: Paginated<ReadyDocumentTemplatePart>,
			allPages: Array<Paginated<ReadyDocumentTemplatePart>>,
		) {
			if (lastPage.items.length < limit) {
				return undefined;
			}

			return allPages.flatMap((page) => page.items).length;
		},
		initialPageParam: 0,
		enabled: templateId !== undefined,
		// Default staleTime is 0: reopening the modal re-enables the query and TanStack
		// refetches every page already in cache in parallel. Keep data fresh until invalidated.
		staleTime: Number.POSITIVE_INFINITY,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
	};
}

export async function generatePresentation(
	request: GenerateDocumentRequest,
): Promise<GenerateDocumentFromTemplatePartResponse> {
	const response = await axios.post<GenerateDocumentFromTemplatePartResponse>(
		`${env.apiUrl}/generated-documents/presentation`,
		request,
	);

	return response.data;
}

export async function generatePresentationDeck(
	source: GenerateDocumentDeckSource,
): Promise<GenerateDocumentFromTemplatePartResponse> {
	const response = await axios.post<GenerateDocumentFromTemplatePartResponse>(
		`${env.apiUrl}/generated-documents/presentation-deck`,
		source,
	);

	return response.data;
}

export const getGeneratedDocuments = queryOptions({
	queryKey: "generatedDocuments",
	async queryFn({ queryKey }) {
		const [, params] = queryKey;
		const response = await axios.get<Paginated<GeneratedDocument>>(
			`${env.apiUrl}/generated-documents`,
			{ params },
		);

		return response.data;
	},
});

export const getGeneratedDocumentFile = queryOptions({
	queryKey: "generatedDocumentFile",
	async queryFn({ queryKey }) {
		const [, { contentId }] = queryKey;
		const response = await axios.get<ArrayBuffer>(
			`${env.apiUrl}/generated-documents/content/${contentId}/download`,
			{ responseType: "arraybuffer" },
		);

		return response.data;
	},
	staleTime: Infinity,
});

export async function deleteGeneratedDocument(id: string): Promise<void> {
	await axios.delete(`${env.apiUrl}/generated-documents/${id}`);
}

export async function renameGeneratedDocument({
	id,
	name,
}: {
	id: string;
	name: string;
}): Promise<void> {
	await axios.put(`${env.apiUrl}/generated-documents/${id}`, { name });
}

export async function deleteDocumentTemplate(id: string): Promise<void> {
	await axios.delete(`${env.apiUrl}/document-templates/${id}`);
}

export async function updateDocumentTemplate(
	documentTemplateId: string,
	request: UpdateDocumentTemplateBody,
): Promise<DocumentTemplate> {
	const response = await axios.patch<DocumentTemplate>(
		`${env.apiUrl}/document-templates/${documentTemplateId}`,
		request,
	);

	return response.data;
}

export async function batchUpdateDocumentTemplateParts(
	documentTemplateId: string,
	parts: UpdateDocumentTemplatePartsBody,
): Promise<void> {
	await axios.patch(`${env.apiUrl}/document-templates/${documentTemplateId}/parts`, parts);
}

export async function setCompanyStyleDocumentTemplate(documentTemplateId: string): Promise<void> {
	await axios.put(`${env.apiUrl}/document-templates/${documentTemplateId}/company-style`);
}

export async function unsetCompanyStyleDocumentTemplate(documentTemplateId: string): Promise<void> {
	await axios.delete(`${env.apiUrl}/document-templates/${documentTemplateId}/company-style`);
}
